
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, Calendar, PiggyBank, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SavingsGoal } from "./GoalCard";

// Types for the goal category and frequency
type GoalCategory = "emergency" | "retirement" | "house" | "car" | "vacation" | "education" | "other";
type ContributionFrequency = "weekly" | "biweekly" | "monthly";

interface GoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (goal: Omit<SavingsGoal, "id" | "currentAmount" | "createdAt"> | SavingsGoal) => void;
  editGoal?: SavingsGoal | null;
}

export const GoalDialog = ({ open, onOpenChange, onSave, editGoal }: GoalDialogProps) => {
  // Form state
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [category, setCategory] = useState<GoalCategory>("emergency");
  const [description, setDescription] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [contributionAmount, setContributionAmount] = useState("");
  const [contributionFrequency, setContributionFrequency] = useState<ContributionFrequency>("monthly");

  // Reset form when dialog opens/closes or editGoal changes
  useEffect(() => {
    if (open) {
      if (editGoal) {
        // Populate form with existing goal data
        setName(editGoal.name);
        setTarget(editGoal.target.toString());
        setCategory(editGoal.category);
        setDescription(editGoal.description || "");
        setTargetDate(editGoal.targetDate ? editGoal.targetDate.toISOString().split('T')[0] : "");
        setContributionAmount(editGoal.contributionAmount ? editGoal.contributionAmount.toString() : "");
        setContributionFrequency(editGoal.contributionFrequency || "monthly");
      } else {
        // Reset form for new goal
        setName("");
        setTarget("");
        setCategory("emergency");
        setDescription("");
        setTargetDate("");
        setContributionAmount("");
        setContributionFrequency("monthly");
      }
    }
  }, [open, editGoal]);

  // Calculate monthly savings needed
  const calculateMonthlySavings = () => {
    if (!target || !targetDate) return null;
    const months = Math.ceil(
      (new Date(targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24 * 30)
    );
    return months > 0 ? parseFloat(target) / months : parseFloat(target);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const goalData = {
      name,
      target: parseFloat(target),
      category,
      description: description || undefined,
      targetDate: targetDate ? new Date(targetDate) : undefined,
      contributionAmount: contributionAmount ? parseFloat(contributionAmount) : undefined,
      contributionFrequency,
    };
    
    // If editing, include the existing id and currentAmount
    if (editGoal) {
      onSave({
        ...goalData,
        id: editGoal.id,
        currentAmount: editGoal.currentAmount,
        createdAt: editGoal.createdAt
      });
    } else {
      onSave(goalData);
    }
    
    onOpenChange(false);
  };

  const monthlySavingsNeeded = calculateMonthlySavings();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Target className="w-5 h-5 text-primary" />
            {editGoal ? "Edit Savings Goal" : "Create New Savings Goal"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Goal Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Goal Category</Label>
            <Select
              value={category}
              onValueChange={(value: GoalCategory) => setCategory(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="emergency">Emergency Fund</SelectItem>
                <SelectItem value="retirement">Retirement</SelectItem>
                <SelectItem value="house">House Down Payment</SelectItem>
                <SelectItem value="car">Car Purchase</SelectItem>
                <SelectItem value="vacation">Vacation</SelectItem>
                <SelectItem value="education">Education</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Goal Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Goal Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Emergency Fund"
              className="border-input"
              required
            />
          </div>

          {/* Goal Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your savings goal..."
              className="resize-none"
            />
          </div>

          {/* Target Amount */}
          <div className="space-y-2">
            <Label htmlFor="target">Target Amount ($)</Label>
            <Input
              id="target"
              type="number"
              min="0"
              step="0.01"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="0.00"
              className="border-input"
              required
            />
          </div>

          {/* Target Date */}
          <div className="space-y-2">
            <Label htmlFor="targetDate">Target Date (Optional)</Label>
            <Input
              id="targetDate"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="border-input"
            />
          </div>

          {/* Contribution Plan */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Contribution Plan</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contributionAmount">Amount ($)</Label>
                <Input
                  id="contributionAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={contributionAmount}
                  onChange={(e) => setContributionAmount(e.target.value)}
                  placeholder="0.00"
                  className="border-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={contributionFrequency}
                  onValueChange={(value: ContributionFrequency) => setContributionFrequency(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Monthly Savings Calculator */}
          {monthlySavingsNeeded && (
            <div className="rounded-lg bg-secondary/10 p-4 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-secondary mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Suggested Monthly Savings</p>
                <p className="text-sm text-muted-foreground">
                  To reach your goal by {new Date(targetDate).toLocaleDateString()}, 
                  you should save approximately ${monthlySavingsNeeded.toFixed(2)} per month.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="submit" className="w-full">
              {editGoal ? "Update Goal" : "Create Goal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
